// Inline "add a stock item" modal, shared by the Inventory dashboard
// (variant='store') and the Costing & Recipes sheet's "New ingredient"
// (variant='admin'). Same fields the dashboard has always shown; the only
// difference between variants is which endpoint (and react-query keys) it uses,
// because managers editing a product are locked out of /store/* and must go
// through the /admin product routes.
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import apiClient from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import { useCurrency } from '@/contexts/CurrencyContext'
import { UNIT_OPTIONS, type UnitMode } from '@/lib/inventory-units'
import { UnitSelector, FormField } from '@/components/store/inventory-form-ui'
import { isLargeStockValue } from '@/components/store/inventory/itemStatus'
import { fmtQty } from '@/lib/quantity'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { IM } from '@/components/store/inventory/itemMasterStrings'
import { ExpiryDateField, ExpiryTrackingFields } from '@/components/store/inventory/ExpiryFields'
import { businessTodayIso, defaultExpiryFor, expiryMissing, parseShelfLife } from '@/lib/expiry'
import { useDayBoundaryHour } from '@/hooks/useDayBoundaryHour'

export interface CreatedStockItem {
  id: string
  name: string
  unit: string
}

interface AddStockItemModalProps {
  open: boolean
  onClose: () => void
  /** 'store' → /store endpoints (admin/inventory_manager); 'admin' → /admin endpoints (admin/manager). */
  variant: 'store' | 'admin'
  /** Pre-fills the Name field — e.g. the product name when opened from the product form. */
  defaultName?: string
  /** Called after a successful create with the new item's id/name/unit. */
  onCreated?: (item: CreatedStockItem) => void
  /**
   * Kitchen flow (store variant only): a scanned code nobody has yet. It is
   * attached to the new item, and Open Food Facts may pre-fill the name — a
   * suggestion the person confirms, never an automatic create.
   */
  defaultBarcode?: string
}

export function AddStockItemModal(props: AddStockItemModalProps) {
  // Remount the inner form each time the modal opens so it picks up a fresh
  // defaultName and clears any prior input.
  if (!props.open) return null
  return <AddStockItemModalInner {...props} />
}

function AddStockItemModalInner({ onClose, variant, defaultName, onCreated, defaultBarcode }: AddStockItemModalProps) {
  const { currencyCode, formatCurrency } = useCurrency()
  const qc = useQueryClient()
  const [form, setForm] = useState({ name: defaultName ?? '', unit: 'each', category_id: '', quantity_on_hand: 0, reorder_level: 0, default_unit_cost: 0, supplier_id: '', notes: '' })
  // Kitchen-flow extras. Rendered (and sent) only for the store variant with
  // the flag on, so every other tenant's modal is exactly what it was.
  const kitchenFlow = useKitchenFlowEnabled() && variant === 'store'
  const [purchaseUnit, setPurchaseUnit] = useState({ label: '', factor: '' })
  const [barcode, setBarcode] = useState(defaultBarcode ?? '')
  const [isReturnable, setIsReturnable] = useState(false)
  const [prefillNote, setPrefillNote] = useState<string | null>(null)
  const { data: external } = useQuery({
    queryKey: ['barcodeExternal', defaultBarcode],
    queryFn: () => apiClient.lookupBarcodeExternal(defaultBarcode!).then((r) => r.data ?? null),
    enabled: kitchenFlow && Boolean(defaultBarcode),
    retry: false,
    staleTime: Infinity,
  })
  useEffect(() => {
    if (!external?.found || !external.name) return
    const suggested = [external.brand, external.name].filter(Boolean).join(' ')
    setForm((f) => (f.name ? f : { ...f, name: suggested.slice(0, 150) }))
    setPrefillNote(IM.barcodes.prefillFound(suggested + (external.quantity_label ? ` (${external.quantity_label})` : '')))
  }, [external])
  const [unitMode, setUnitMode] = useState<UnitMode>('quantity')
  // Expiry tracking (F-CTRL-N73). The opening quantity is stock like any
  // other: a tracked item that arrives holding some needs its date.
  const [tracksExpiry, setTracksExpiry] = useState(false)
  const [shelfLife, setShelfLife] = useState('')
  const [typedExpiry, setTypedExpiry] = useState<string | null>(null)
  const today = businessTodayIso(useDayBoundaryHour())
  const shelf = parseShelfLife(shelfLife)
  const tracked = { tracks_expiry: tracksExpiry, shelf_life_days: shelf.days ?? null }
  const hasOpeningStock = form.quantity_on_hand > 0
  const openingExpiry = typedExpiry ?? defaultExpiryFor(tracked, today)
  const expiryBlocked = Boolean(shelf.error) || (hasOpeningStock && expiryMissing(tracked, openingExpiry))
  // Outlier guard: creating a six-figure asset in one click is exactly how
  // 800 kg of condensed milk (Rs 799,940 — 81.5% of an entire inventory)
  // slipped in unchallenged. Same threshold as the count dialog's loud
  // confirm; the second click is the whole safeguard.
  const [outlierAcknowledged, setOutlierAcknowledged] = useState(false)
  const openingValue = form.quantity_on_hand * form.default_unit_cost
  const isOutlier = isLargeStockValue(form.quantity_on_hand, form.default_unit_cost)

  const { data: categoriesRes } = useQuery({
    queryKey: variant === 'admin' ? ['admin-stock-categories'] : ['stockCategories'],
    queryFn: () => (variant === 'admin' ? apiClient.getStockCategoriesViaAdmin() : apiClient.getStockCategories()),
  })
  const categories = categoriesRes?.data ?? []

  // Vendor picker, on both variants. It used to be store-only because the sole
  // supplier list sat behind the /store inventory_manager gate while this modal's
  // admin variant (the product form) may be a manager — so an item created while
  // turning on inventory tracking came out with no vendor and had to be re-edited
  // from the Inventory screen. /admin/products/suppliers serves the same list
  // under the admin+manager gate. Separate cache keys: same shape, but different
  // endpoints behind different gates, so they must not share an entry.
  const { data: suppliersRes } = useQuery({
    queryKey: variant === 'admin' ? ['admin-suppliers'] : ['suppliers'],
    queryFn: () => (variant === 'admin' ? apiClient.listSuppliersViaAdmin() : apiClient.listSuppliers()),
  })
  const suppliers = suppliersRes?.data ?? []

  const mut = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        category_id: form.category_id || undefined,
        default_unit_cost: form.default_unit_cost || undefined,
        supplier_id: form.supplier_id || undefined,
        notes: form.notes || undefined,
        ...(tracksExpiry ? { tracks_expiry: true, ...(shelf.days ? { shelf_life_days: shelf.days } : {}) } : {}),
        ...(hasOpeningStock && openingExpiry ? { opening_expiry_date: openingExpiry } : {}),
      }
      if (variant === 'admin') return apiClient.createStockItemViaAdmin(payload)
      if (!kitchenFlow) return apiClient.createStockItem(payload)
      const factor = Number(purchaseUnit.factor)
      const res = await apiClient.createStockItem({
        ...payload,
        ...(purchaseUnit.label.trim() && factor > 0 ? { purchase_unit_label: purchaseUnit.label.trim(), purchase_unit_factor: factor } : {}),
        ...(isReturnable ? { is_returnable: true } : {}),
      })
      const id = res?.data?.id
      if (id && barcode.trim()) {
        try {
          await apiClient.addStockItemBarcode(id, { barcode: barcode.trim() })
        } catch (e) {
          // The item exists; only the code failed (e.g. taken meanwhile). Say
          // so rather than failing the whole create.
          toastHelpers.error('Barcode not added', (e as Error).message)
        }
      }
      return res
    },
    onSuccess: (res) => {
      // Refresh both the inventory screen and the product picker, whichever is mounted.
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] }) // F-FE-N118: qty 0 + reorder level = an alert right away
      qc.invalidateQueries({ queryKey: ['stockCategories'] })
      qc.invalidateQueries({ queryKey: ['admin-stock-categories'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      qc.invalidateQueries({ queryKey: ['admin-stock-item-options'] })
      qc.invalidateQueries({ queryKey: ['expiringBatches'] }) // an opening lot can already be close to its date
      toastHelpers.success('Inventory', `"${form.name}" added to inventory`)
      const id = res?.data?.id
      if (id && onCreated) onCreated({ id, name: form.name, unit: form.unit })
      onClose()
    },
    onError: (err: Error) => toastHelpers.error('Add item failed', err.message || 'Failed to add item'),
  })

  // A Radix Dialog, not a hand-rolled overlay: opened from inside another Radix
  // layer (the Costing & Recipes sheet), a plain portal sits outside that
  // layer, so the sheet's focus trap stole every keystroke and its
  // click-outside closed the sheet — taking this modal with it. As a Dialog it
  // stacks on top and owns focus and outside-clicks. Its portal also keeps
  // this <form> out of any parent <form> (nested forms trigger a native reload
  // that aborts the create).
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent showCloseButton={false} aria-describedby={undefined} className="block max-w-lg gap-0 p-0">
        <form onSubmit={e => { e.preventDefault(); e.stopPropagation(); mut.mutate() }} className="p-6 space-y-4">
          <div className="flex items-center justify-between mb-2">
            <DialogTitle>Add Stock Item</DialogTitle>
            <button type="button" onClick={onClose} aria-label="Close"><X className="w-5 h-5" /></button>
          </div>
          <FormField label="Name" required><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></FormField>
          {kitchenFlow && prefillNote && <p className="-mt-2 text-xs text-muted-foreground">{prefillNote}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Measured By">
              <UnitSelector unitMode={unitMode} unit={form.unit} onModeChange={mode => { setUnitMode(mode); setForm(f => ({ ...f, unit: UNIT_OPTIONS[mode][0].value })) }} onUnitChange={u => setForm(f => ({ ...f, unit: u }))} />
            </FormField>
            <FormField label="Category">
              <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
                <option value="">None</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </FormField>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormField label="Qty on Hand"><input type="number" step="any" min="0" value={form.quantity_on_hand} onChange={e => { setForm(f => ({ ...f, quantity_on_hand: Math.max(0, +e.target.value) })); setOutlierAcknowledged(false) }} /></FormField>
            <FormField label="Warn when below"><input type="number" step="any" min="0" value={form.reorder_level} onChange={e => setForm(f => ({ ...f, reorder_level: Math.max(0, +e.target.value) }))} /></FormField>
            <FormField label={`Unit Cost (${currencyCode})`}><input type="number" step="0.01" min="0" value={form.default_unit_cost} onChange={e => { setForm(f => ({ ...f, default_unit_cost: Math.max(0, +e.target.value) })); setOutlierAcknowledged(false) }} /></FormField>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">0 in “Warn when below” means no low-stock warnings for this item.</p>
          {isOutlier && (
            <label className="flex items-start gap-2 rounded-md border border-warning-border bg-warning-surface/40 px-3 py-2 text-sm cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={outlierAcknowledged} onChange={e => setOutlierAcknowledged(e.target.checked)} />
              <span>
                You&apos;re creating <span className="font-semibold">{fmtQty(form.quantity_on_hand)} {form.unit}</span> worth about{' '}
                <span className="font-semibold">{formatCurrency(openingValue)}</span>. Double-check the quantity and the unit ({form.unit}) — this is how an 800&nbsp;kg typo becomes 80% of an inventory.
              </span>
            </label>
          )}
          <ExpiryTrackingFields tracks={tracksExpiry} shelfLife={shelfLife} onTracksChange={setTracksExpiry} onShelfLifeChange={setShelfLife} />
          {hasOpeningStock && (
            <ExpiryDateField
              id="add-item-expiry"
              item={tracked}
              value={openingExpiry}
              onChange={setTypedExpiry}
              prefilled={typedExpiry === null}
            />
          )}
          {kitchenFlow && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label={IM.purchaseUnit.labelLabel}>
                  <input value={purchaseUnit.label} placeholder={IM.purchaseUnit.labelPlaceholder} maxLength={40}
                    onChange={e => setPurchaseUnit(p => ({ ...p, label: e.target.value }))} />
                </FormField>
                <FormField label={IM.purchaseUnit.factorLabel(form.unit)}>
                  <input type="number" step="any" min="0" value={purchaseUnit.factor}
                    onChange={e => setPurchaseUnit(p => ({ ...p, factor: e.target.value }))} />
                </FormField>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">{IM.purchaseUnit.help}</p>
              <FormField label={IM.barcodes.codeLabel}>
                <input value={barcode} inputMode="numeric" autoComplete="off" onChange={e => setBarcode(e.target.value)} />
              </FormField>
              <label className="flex min-h-11 cursor-pointer items-start gap-3 rounded-md border p-3">
                <input type="checkbox" className="mt-1 h-5 w-5" checked={isReturnable} onChange={e => setIsReturnable(e.target.checked)} />
                <span><span className="font-medium">{IM.returnable.label}</span><span className="block text-xs text-muted-foreground">{IM.returnable.help}</span></span>
              </label>
            </>
          )}
          <FormField label="Vendor / supplier">
            <select value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))}>
              <option value="">None</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.commission_percent ? ` (${s.commission_percent}% commission)` : ''}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Notes"><textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></FormField>
          <Button type="submit" className="w-full" disabled={mut.isPending || !form.name || expiryBlocked || (isOutlier && !outlierAcknowledged) || (kitchenFlow && Boolean(purchaseUnit.label.trim()) !== (Number(purchaseUnit.factor) > 0))}>{mut.isPending ? 'Saving...' : 'Add Item'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
